import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion'; // Add AnimatePresence
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { signInWithEmailAndPassword, getAuth, setPersistence, browserLocalPersistence, onAuthStateChanged } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTachometerAlt, faCheckCircle, faBrain, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import Footer from './Footer';
import Login from './Login';

let ChartDataLabels;
try {
  ChartDataLabels = require('chartjs-plugin-datalabels');
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);
} catch (e) {
  console.warn('chartjs-plugin-datalabels not installed, chart will render without data labels.');
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
}

const auth = getAuth();

function LandingPage() {
  const [email, setEmail] = useState('');
  const [subscribeError, setSubscribeError] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const accuracyRef = useRef(null);
  const featuresRef = useRef(null);
  const contactRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserRole(userSnap.data().role);
        }
      } else {
        setUserRole(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (location.hash === "#accuracy" && accuracyRef.current) {
      accuracyRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (location.hash === "#features" && featuresRef.current) {
      featuresRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (location.hash === "#contact" && contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubscribeError('Please enter a valid email address.');
      return;
    }
    try {
      await addDoc(collection(db, 'subscribers'), { email, timestamp: new Date() });
      setSubscribeError('');
      alert(`Subscribed with email: ${email}`);
      setEmail('');
    } catch (err) {
      setSubscribeError('Failed to subscribe: ' + err.message);
    }
  };

  // This can either open modal or navigate to login page
  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };

  const accuracyData = {
    labels: ['Pneumonia', 'Brain Tumor'],
    datasets: [
      {
        label: 'Accuracy (%)',
        data: [95, 99],
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          gradient.addColorStop(0, context.datasetIndex === 0 ? 'rgba(54, 162, 235, 0.8)' : 'rgba(255, 99, 132, 0.8)');
          gradient.addColorStop(1, context.datasetIndex === 0 ? 'rgba(54, 162, 235, 0.4)' : 'rgba(255, 99, 132, 0.4)');
          return gradient;
        },
        borderColor: (context) => (context.datasetIndex === 0 ? 'rgba(54, 162, 235, 1)' : 'rgba(255, 99, 132, 1)'),
        borderWidth: 2,
        borderRadius: 10,
        barThickness: 50,
        shadowOffsetX: 5,
        shadowOffsetY: 5,
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, 0.2)',
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: ChartDataLabels
        ? {
          anchor: 'end',
          align: 'top',
          color: 'black',
          font: { family: 'Noto Sans', weight: 'bold', size: 14 },
          formatter: (value) => `${value}%`,
          offset: 4,
        }
        : undefined,
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        ticks: { color: 'black', font: { family: 'Noto Sans', size: 14 } },
        grid: { display: false },
        border: { color: 'black' },
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          color: 'black',
          font: { family: 'Noto Sans', size: 14 },
          callback: (value) => `${value}%`,
        },
        grid: { color: 'rgba(0, 0, 0, 0.1)', borderDash: [5, 5] },
        border: { color: 'black' },
      },
    },
    maintainAspectRatio: false,
    layout: { padding: { top: 20, bottom: 20 } },
    barPercentage: 0.6,
    categoryPercentage: 0.8,
    animation: { duration: 1000, easing: 'easeInOutCubic' },
  };

  return (
    <div className="min-h-screen bg-[#d3d4d6] text-black flex flex-col items-center justify-between font-['Noto Sans']">
      <Header handleLoginRedirect={handleLoginRedirect} fadeIn={fadeIn} />

      {/* Wrap the Login component in AnimatePresence for proper animations */}
      <AnimatePresence>
        {isLoginModalOpen && (
          <Login key="login-modal" onClose={() => setIsLoginModalOpen(false)} />
        )}
      </AnimatePresence>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-6xl w-full mt-16 mb-16 border border-gray-300 transform hover:scale-105 transition-transform duration-300"
      >
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2">
            <h1 className="text-4xl font-bold mb-6 text-black leading-tight">
              Predicating Using AI<br />MRI & X-Ray
            </h1>
            <p className="text-black mb-8 leading-relaxed">
              Discover advanced X-ray analysis for precise pneumonia detection. Our platform uses cutting-edge technology to enhance medical diagnostics, ensuring accuracy and efficiency for healthcare professionals worldwide.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-black text-white px-8 py-3 rounded-full hover:bg-gray-800 transition-colors duration-300 font-semibold"
            >
              Read More
            </motion.button>
          </div>
          <div className="md:w-1/2 mt-8 md:mt-0 flex justify-center">
            <img
              src="https://www.kindpng.com/picc/m/71-714262_vector-illustration-of-health-care-professional-doctor-x.png"
              alt="Healthcare Professional X-ray"
              className="rounded-2xl shadow-md w-full max-w-md"
            />
          </div>
        </div>
      </motion.div>

      <div className="w-full max-w-6xl space-y-12 mb-16">
        <motion.section
          ref={featuresRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          id="features"
          className="border-t border-b border-gray-300 p-8"
        >
          <h2 className="text-4xl font-bold mb-6 text-gray-600">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6 text-center">
              <FontAwesomeIcon icon={faTachometerAlt} className="text-6xl text-green-600 mb-4" />
              <p className="text-lg font-semibold">Get Fast Result <br /> Just one Click</p>
            </div>
            <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6 text-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-600 mb-4" />
              <p className="text-lg font-semibold">High Accuracy <br /> Case Prediction</p>
            </div>
            <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6 text-center">
              <FontAwesomeIcon icon={faBrain} className="text-6xl text-green-600 mb-4" />
              <p className="text-lg font-semibold">Best ML Model <br /> for X-ray and MRI</p>
            </div>
            <div className="bg-white rounded-lg shadow-md border border-gray-300 p-6 text-center">
              <FontAwesomeIcon icon={faShieldAlt} className="text-6xl text-green-600 mb-4" />
              <p className="text-lg font-semibold">Secure Data <br /> No Need Details</p>
            </div>
          </div>
        </motion.section>

        <motion.section
          ref={accuracyRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          id="accuracy"
          className="border-t border-b border-gray-300 p-8"
        >
          <h2 className="text-4xl font-bold mb-6 text-gray-600">Prediction Accuracy</h2>
          <div className="flex flex-col md:flex-row gap-12">
            <div className="md:w-1/2">
              <p className="text-black text-justify leading-tight text-lg space-y-6">
                Our state-of-the-art diagnostic model ensures precision with robust datasets. For pneumonia, we trained on over 6,000 high-resolution X-ray images, achieving 95% accuracy. For brain tumors, 7,035 MRI/CT scans yielded 99% accuracy. Expert-curated, these datasets use CNNs and transfer learning. Cross-validation and data augmentation minimize bias, ensuring reliable diagnostics. This empowers healthcare providers with actionable insights for critical decisions.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="mt-6 bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition-colors duration-300 font-semibold"
              >
                Read More
              </motion.button>
            </div>
            <div className="md:w-1/2 h-80 bg-white rounded-xl shadow-lg p-4">
              <Bar data={accuracyData} options={chartOptions} />
            </div>
          </div>
        </motion.section>

        <motion.section
          ref={contactRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          id="contact"
          className="relative p-8 overflow-hidden bg-white shadow-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black to-gray-500 animate-gradient" />
          <div className="relative container mx-auto flex flex-col md:flex-row items-center justify-between gap-12 text-center">
            <div className="md:w-1/2 transition-transform">
              <h2 className="text-4xl font-bold mb-6 text-gray-100">Contact Us</h2>
              <p className="text-gray-200 text-lg leading-relaxed">
                Have questions or need support? Reach out to us! We’re here to assist with any inquiries about Medpredix.
              </p>
              <Link to="/contact-us">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-6 bg-gray-700 text-white px-6 py-3 rounded-full hover:bg-green-800 transition-transform duration-300 font-semibold shadow-2xl"
                >
                  Contact Form
                </motion.button>
              </Link>
            </div>
            <div className="hidden md:block h-24 w-px bg-gray-300"></div>
            <div className="md:w-1/2 transition-transform">
              <h2 className="text-4xl font-bold mb-6 text-gray-100">Ready to Join Us?</h2>
              <p className="text-gray-200 text-lg leading-relaxed">
                Become a part of our growing community. Apply now and start your journey with us!
              </p>
              <Link to="/join-request">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-6 bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-transform duration-300 font-semibold shadow-2xl"
                >
                  Join Us
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>

      <Footer
        email={email}
        setEmail={setEmail}
        handleSubscribe={handleSubscribe}
        subscribeError={subscribeError}
        fadeIn={fadeIn}
      />
    </div>
  );
}


export default LandingPage;